import { useQuery } from '@tanstack/react-query';
import type { TooltipModel } from 'chart.js';
import commaNumber from 'comma-number';
import dayjs from 'dayjs';
import { useMemo, useRef, useState } from 'react';

import Spinner from '@/components/Common/Spinner';
import { Stage } from '@/components/Stage/Stage';
import { ToggleStage } from '@/components/Stage/ToggleStage';
import TabRadio from '@/components/TabRadio/TabRadio';
import {
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_FREQUENCY,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import TimeSeriesStackedBarChart from '@/features/(charts)/chart-renderer/ui/Single/TimeSeriesStackedBarChart';
import {
  convertSignalDatePresetToChartFrequency,
  convertSignalDatePresetToChartTerm,
} from '@/features/(signal)/signals/lib';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '@/features/(signal)/signals/model';
import { SIGNAL_DATE_PRESET } from '@/features/(signal)/signals/model';
import service from '@/services';
import zIndex from '@/styles/zIndex';
import { getQuadrant } from '@/utils/dom';

type Props = {
  issue: CompositedSignalIssue;
  filterSet: StructuredFilter;
  datePreset: SignalDateSelectionInput;
};

export const VolumeChangeChartArea = (props: Props) => {
  const [chartType, setChartType] = useState<string>('volume');
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-semibold">Trends</h3>
        <TabRadio
          options={[
            {
              node: <div>Volume</div>,
              key: 'volume',
            },
            {
              node: <div>Sentiments</div>,
              key: 'sentiments',
            },
            {
              node: <div>Source</div>,
              key: 'source',
            },
          ]}
          value={chartType}
          onChange={setChartType}
        />
      </div>
      <Stage className="p-4 rounded-2xl border border-border">
        <Stage.Scene status={chartType === 'volume'}>
          <VolumeTrendChart {...props} />
        </Stage.Scene>
        <Stage.Scene status={chartType === 'sentiments'}>
          <SentimentsTrendChart {...props} />
        </Stage.Scene>
        <Stage.Scene status={chartType === 'source'}>
          <SourceTrendChart {...props} />
        </Stage.Scene>
      </Stage>
    </div>
  );
};

const VolumeTrendChart = (props: Props) => {
  const chart: ChartBase & { name: string } = useMemo(() => {
    return getCommonChart({
      name: 'source-trend-chart',
      fieldType: CHART_FIELD.FEEDBACK_ALL,
      datePreset: props.datePreset,
      filter: props.filterSet,
      issue: props.issue,
    });
  }, [props.datePreset, props.filterSet, props.issue]);

  const { data, isFetching } = useQuery({
    queryKey: ['volume-trend-chart', chart],
    queryFn: () => service.chart.fetchTable(chart),
  });

  return (
    <ToggleStage
      status={!isFetching}
      data={data}
      className="w-full h-[400px] relative"
      fallback={
        <div className="flex items-center justify-center h-[400px]">
          <Spinner />
        </div>
      }
    >
      {(data) => (
        <TimeSeriesStackedBarChart
          chart={chart}
          tableData={data}
          xAxisTickInterval={getXAxisTickInterval(props.datePreset)}
          customTooltip={SignalBarTooltip}
        />
      )}
    </ToggleStage>
  );
};
const SentimentsTrendChart = (props: Props) => {
  const chart: ChartBase & { name: string } = useMemo(() => {
    return getCommonChart({
      name: 'sentiments-trend-chart',
      fieldType: CHART_FIELD.FEEDBACK_SENTIMENT,
      datePreset: props.datePreset,
      filter: props.filterSet,
      issue: props.issue,
    });
  }, [props.datePreset, props.filterSet, props.issue]);

  const { data, isFetching } = useQuery({
    queryKey: ['sentiments-trend-chart', chart],
    queryFn: () => service.chart.fetchTable(chart),
  });

  return (
    <ToggleStage
      status={!isFetching}
      data={data}
      className="w-full h-[400px] relative"
      fallback={
        <div className="flex items-center justify-center h-[400px]">
          <Spinner />
        </div>
      }
    >
      {(data) => (
        <TimeSeriesStackedBarChart
          chart={chart}
          tableData={data}
          xAxisTickInterval={getXAxisTickInterval(props.datePreset)}
          customTooltip={SignalBarTooltip}
        />
      )}
    </ToggleStage>
  );
};
const SourceTrendChart = (props: Props) => {
  const chart: ChartBase & { name: string } = useMemo(() => {
    return getCommonChart({
      name: 'source-trend-chart',
      fieldType: CHART_FIELD.FEEDBACK_SOURCE,
      datePreset: props.datePreset,
      filter: props.filterSet,
      issue: props.issue,
    });
  }, [props.datePreset, props.filterSet, props.issue]);

  const { data, isFetching } = useQuery({
    queryKey: ['source-trend-chart', chart],
    queryFn: () => service.chart.fetchTable(chart),
  });

  return (
    <ToggleStage
      status={!isFetching}
      data={data}
      className="w-full h-[400px] relative"
      fallback={
        <div className="flex items-center justify-center h-[400px]">
          <Spinner />
        </div>
      }
    >
      {(data) => (
        <TimeSeriesStackedBarChart
          chart={chart}
          tableData={data}
          xAxisTickInterval={getXAxisTickInterval(props.datePreset)}
          customTooltip={SignalBarTooltip}
        />
      )}
    </ToggleStage>
  );
};

// tooltip 날짜 라벨 가독성 포맷
const formatTooltipDateLabel = (
  rawLabel: string,
  frequency: CHART_FREQUENCY | null,
): string => {
  if (!frequency) return rawLabel;
  switch (frequency) {
    case CHART_FREQUENCY.HOUR: {
      const parsed = dayjs(rawLabel, 'YYYY-MM-DD HH');
      if (!parsed.isValid()) return rawLabel;
      return parsed.format('MMM D, h:00 A');
    }
    case CHART_FREQUENCY.DAY: {
      const parsed = dayjs(rawLabel, 'YYYY-MM-DD');
      if (!parsed.isValid()) return rawLabel;
      return parsed.format('MMM D, YYYY');
    }
    default:
      return rawLabel;
  }
};

// Signal 전용 tooltip 컴포넌트
const SignalBarTooltip = (props: {
  tooltip: TooltipModel<'line'>;
  meta: ChartBase & { name: string };
  tableData: ChartTableRes;
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  const pos = useMemo(() => {
    const chartRect = props.tooltip.chart.canvas.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    const tooltipWidth = tooltipRect?.width || 200;
    const tooltipHeight = tooltipRect?.height || 100;

    const quadrant = getQuadrant(
      { width: chartRect.width, height: chartRect.height },
      {
        top: props.tooltip._eventPosition.y,
        left: props.tooltip._eventPosition.x,
      },
    );

    const center = {
      x: props.tooltip._eventPosition.x - tooltipWidth / 2,
      y: props.tooltip._eventPosition.y - tooltipHeight / 2,
      x2: props.tooltip._eventPosition.x + tooltipWidth / 2,
      y2: props.tooltip._eventPosition.y + tooltipHeight / 2,
    };

    const offset = 10;
    switch (quadrant) {
      case 1:
        return {
          right:
            center.x2 > chartRect.width - offset
              ? offset
              : chartRect.width - center.x2,
          top: center.y < offset ? offset : center.y,
        };
      case 2:
        return {
          left: center.x < offset ? offset : center.x,
          top: center.y < offset ? offset : center.y,
        };
      case 3:
        return {
          left: center.x < offset ? offset : center.x,
          bottom:
            center.y2 > chartRect.height - offset
              ? offset
              : chartRect.height - center.y2,
        };
      case 4:
        return {
          right:
            center.x2 > chartRect.width - offset
              ? offset
              : chartRect.width - center.x2,
          bottom:
            center.y2 > chartRect.height - offset
              ? offset
              : chartRect.height - center.y2,
        };
    }
  }, [props.tooltip]);

  const dataPoints = props.tooltip.dataPoints;
  const rawLabel = dataPoints[0]?.label || '';
  const dateLabel = formatTooltipDateLabel(
    rawLabel,
    props.meta.historical.frequency,
  );

  return (
    <div
      ref={tooltipRef}
      className="absolute bg-white border border-gray-200 rounded-lg p-3 shadow-md min-w-[180px] pointer-events-none animate-in fade-in duration-200"
      style={{ ...pos, zIndex: zIndex.chartTooltip }}
    >
      <p className="text-xs font-medium text-gray-500 pb-2 border-b border-gray-100 mb-2">
        {dateLabel}
      </p>
      <div className="flex flex-col gap-1">
        {dataPoints.map((point, i) => (
          <div key={i} className="flex items-center gap-2 py-0.5">
            <div
              className="size-3 rounded-sm shrink-0"
              style={{
                backgroundColor: point.dataset.backgroundColor as string,
              }}
            />
            <span className="text-xs text-gray-700 font-medium">
              {point.dataset.label}:{' '}
              {commaNumber(Number(Number(point.raw).toFixed(1)) || 0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// datePreset별 x축 tick 간격 반환
const getXAxisTickInterval = (
  datePreset: SignalDateSelectionInput,
): number => {
  const preset =
    typeof datePreset === 'string'
      ? datePreset
      : datePreset.type === 'preset'
        ? datePreset.preset
        : null;
  switch (preset) {
    case SIGNAL_DATE_PRESET.PAST_24_HOURS:
      return 3; // 24시간 → 3시간 간격 (8개 tick)
    case SIGNAL_DATE_PRESET.PAST_3_DAYS:
      return 6; // 72시간 → 6시간 간격 (12개 tick)
    case SIGNAL_DATE_PRESET.PAST_7_DAYS:
    case SIGNAL_DATE_PRESET.PAST_30_DAYS:
    case SIGNAL_DATE_PRESET.PAST_90_DAYS:
      return 1; // Daily 데이터는 모든 레이블 표시
    default:
      return 1;
  }
};

const getCommonChart = ({
  name,
  fieldType,
  datePreset,
  filter,
  issue,
}: {
  name: string;
  fieldType: CHART_FIELD;
  datePreset: SignalDateSelectionInput;
  filter: StructuredFilter;
  issue: CompositedSignalIssue;
}): ChartBase & { name: string } => {
  // filter에 이미 UTC로 변환된 start_date/end_date가 포함되어 있음
  // 날짜 필터를 제거하지 않고 그대로 전달하여 일관된 기간 데이터 조회
  return {
    name,
    field: {
      candidates: [],
      entire: true,
      type: fieldType,
    },
    graph: CHART_GRAPH.BAR_STACKED,
    graph_direction: CHART_GRAPH_DIRECTION.VERTICAL,
    historical: {
      frequency: convertSignalDatePresetToChartFrequency(datePreset),
      value: true,
    },
    type: CHART_TYPE.SINGLE_AXIS,
    selected_field: {
      method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_10,
      items: [],
    },
    target: {
      is_compare: false,
      term: convertSignalDatePresetToChartTerm(datePreset),
      filter: filter,
    },
    series: [
      {
        measure: {
          metric: CHART_METRIC.FEEDBACK,
          calculation: CHART_CALCULATION.COUNT,
        },
      },
    ],
  };
};
